import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Grid3x3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../../components/SEO";
import ToolShell, { Field, inputCls } from "../../components/ToolShell";
import { api } from "../../lib/api";

export default function LoShuGrid() {
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!dob) return toast.error("Please enter your date of birth.");
    setLoading(true);
    try {
      const { data } = await api.post("/tools/loshu", { dob });
      setResult(data);
      setTimeout(() => document.getElementById("loshu-result")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const chip = (arr) => arr.length ? arr.map((n) => (
    <span key={n} className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary font-serif text-lg">{n}</span>
  )) : <span className="text-sm text-muted-foreground">None</span>;

  return (
    <ToolShell slug="lo-shu-grid" name="Lo Shu Grid" tagline="Map your missing energies">
      <SEO title="Lo Shu Grid Calculator" description="Generate your Lo Shu grid from your date of birth. Discover missing numbers, strong numbers, planes and simple remedies." path="/tools/lo-shu-grid" />

      <form onSubmit={submit} className="max-w-md rounded-3xl border border-border bg-card p-7 shadow-soft" data-testid="loshu-form">
        <Field label="Date of Birth">
          <input type="date" className={inputCls} value={dob} onChange={(e) => setDob(e.target.value)} data-testid="loshu-dob-input" />
        </Field>
        <button disabled={loading} data-testid="loshu-submit"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Grid3x3 className="h-4 w-4 text-gold" />}
          {loading ? "Building your grid…" : "Generate grid"}
        </button>
      </form>

      {result && (
        <motion.div id="loshu-result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-14" data-testid="loshu-result">
          <div className="grid gap-8 md:grid-cols-2 items-start">
            <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
              <div className="grid grid-cols-3 gap-3">
                {result.grid.flat().map((cell, i) => (
                  <div key={i} className={`flex aspect-square items-center justify-center rounded-2xl border text-center font-serif text-2xl ${cell.count ? "border-primary/30 bg-secondary text-primary" : "border-dashed border-border text-muted-foreground/40"}`}>
                    {cell.display}
                  </div>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl bg-secondary p-3 text-center"><p className="text-xs text-muted-foreground">Driver</p><p className="font-serif text-2xl text-primary">{result.driver}</p></div>
                <div className="rounded-xl bg-secondary p-3 text-center"><p className="text-xs text-muted-foreground">Destiny</p><p className="font-serif text-2xl text-primary">{result.destiny}</p></div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                <h3 className="font-serif text-xl">Strong Numbers</h3>
                <div className="mt-3 flex flex-wrap gap-2">{chip(result.strong_numbers)}</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                <h3 className="font-serif text-xl">Missing Numbers</h3>
                <div className="mt-3 flex flex-wrap gap-2">{chip(result.missing_numbers)}</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                <h3 className="font-serif text-xl">Planes & Arrows</h3>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {result.planes.map((p) => (
                    <li key={p.name} className="flex items-center justify-between">
                      <span className="text-muted-foreground">{p.name}</span>
                      <span className={p.complete ? "text-gold" : "text-muted-foreground/50"}>{p.complete ? "Complete" : "—"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <h3 className="font-serif text-xl">Grid Analysis & Advantages</h3>
              <ul className="mt-3 space-y-2">
                {result.advantages.map((a, i) => <li key={i} className="flex gap-2 text-sm text-muted-foreground"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{a}</li>)}
              </ul>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <h3 className="font-serif text-xl">Improving Missing Numbers</h3>
              <ul className="mt-3 space-y-3">
                {result.missing_meanings.length ? result.missing_meanings.map((m) => (
                  <li key={m.number} className="text-sm"><span className="font-medium text-primary">{m.number} — </span><span className="text-muted-foreground">{m.meaning}. </span><span className="text-foreground italic">Remedy: {m.remedy}</span></li>
                )) : <li className="text-sm text-muted-foreground">Wonderful — your grid has no missing numbers!</li>}
              </ul>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft"><h3 className="font-serif text-xl">Career Suggestions</h3><p className="mt-2 text-sm text-muted-foreground">{result.career_suggestion}</p></div>
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft"><h3 className="font-serif text-xl">Personality</h3><p className="mt-2 text-sm text-muted-foreground">{result.personality}</p></div>
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft md:col-span-2"><h3 className="font-serif text-xl">Relationships</h3><p className="mt-2 text-sm text-muted-foreground">{result.relationship}</p></div>
          </div>
        </motion.div>
      )}

      <section className="mt-20 max-w-3xl" data-testid="loshu-explainer">
        <h2 className="font-serif text-3xl">What is a Lo Shu grid?</h2>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          The Lo Shu grid is a 3×3 square of numbers used in numerology. Your date of birth is placed into the grid digit by digit. Numbers that appear are your strong energies, and numbers that do not appear are your missing numbers.
        </p>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          The name comes from an old Chinese legend about a 3×3 magic square. Indian numerology uses the same idea with its own layout and its own readings.
        </p>

        <h2 className="mt-12 font-serif text-3xl">How the grid is built</h2>
        <ul className="mt-4 space-y-2 text-muted-foreground leading-relaxed">
          <li>Take the digits of your date of birth. Zeros are left out.</li>
          <li>Place each digit in its square. If a digit appears twice, it appears twice in the same square.</li>
          <li>Squares with no digit are your missing numbers.</li>
          <li>Your Driver (Mulank) and Destiny (Bhagyank) numbers are shown next to the grid. Find yours with the <Link to="/tools/numerology-calculator" className="text-primary underline">numerology calculator</Link>.</li>
        </ul>

        <h2 className="mt-12 font-serif text-3xl">Worked example: 14 August 1995</h2>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          The date is 14-08-1995, so the digits are 1, 4, 0, 8, 1, 9, 9, 5. Remove the zero and you have 1, 4, 8, 1, 9, 9, 5. The number 1 appears twice and 9 appears twice. The numbers 4, 5 and 8 appear once each. The missing numbers are 2, 3, 6 and 7.
        </p>

        <h2 className="mt-12 font-serif text-3xl">What each number is said to represent</h2>
        <ul className="mt-4 space-y-2 text-muted-foreground leading-relaxed">
          <li><strong className="text-foreground">1:</strong> leadership and independence</li>
          <li><strong className="text-foreground">2:</strong> sensitivity and cooperation</li>
          <li><strong className="text-foreground">3:</strong> creativity and self-expression</li>
          <li><strong className="text-foreground">4:</strong> discipline and structure</li>
          <li><strong className="text-foreground">5:</strong> communication and change</li>
          <li><strong className="text-foreground">6:</strong> care and responsibility</li>
          <li><strong className="text-foreground">7:</strong> reflection and analysis</li>
          <li><strong className="text-foreground">8:</strong> ambition and practical drive</li>
          <li><strong className="text-foreground">9:</strong> compassion and idealism</li>
        </ul>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          These are traditional themes. Different numerologists read them in slightly different ways.
        </p>

        <h2 className="mt-12 font-serif text-3xl">Frequently asked questions</h2>
        <div className="mt-6 space-y-6">
          <div>
            <h3 className="font-serif text-xl">Is a missing number bad?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              No. A missing number is read as an energy that may feel less natural to you, not as a flaw. The suggested remedies are simple habits for reflection.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Do zeros count in the grid?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">No. Zeros are left out because the grid only uses the digits 1 to 9.</p>
          </div>
          <div>
            <h3 className="font-serif text-xl">What are planes and arrows?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              A plane or arrow is a row, column or diagonal of the grid. When all its squares are filled, it is read as a strength. The tool marks complete ones for you.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Is this Lo Shu grid calculator free?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">Yes. Enter your date of birth to generate your grid.</p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Can the Lo Shu grid predict my future?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              No. It is a tool for reflection and self-understanding, not a guarantee of any outcome.
            </p>
          </div>
        </div>
      </section>
    </ToolShell>
  );
}